import React, { useMemo, useState } from 'react';
import _ from 'lodash';

import { SwitchInput } from '../../common/SwitchInput';
import { buildD } from './fusebox-layout-editor/svg-path-import.mjs';

const shapeColor = (i, n) => `hsl(${Math.round((i * 360) / Math.max(n, 1))} 80% 55%)`;

/**
 * Shown when a pasted/dropped SVG has several drawable elements. Lets the user pick which ones to
 * import and whether each becomes its own svg layer (default, "one svg layer per <path>") or all
 * of them merge into a single layer (useful for a silhouette split into pieces).
 *
 * @param doc {{shapes: object[], viewBox: object|null}} result of parseSvgDocument
 * @param onImport (shapes, merge) => void
 */
export default function SvgImportModal({ doc, onImport, onCancel }) {
  const { shapes } = doc;
  const [selected, setSelected] = useState(() => shapes.map(() => true));
  const [merge, setMerge] = useState(false);
  const [hovered, setHovered] = useState(null);

  // Preview what will actually be imported (the vertex+radius interpretation), not the source markup
  const previews = useMemo(() => shapes.map(s => s.subpaths.map(sp => buildD(sp.points, sp.isClosed)).join(' ')), [shapes]);

  const previewBox = useMemo(() => {
    let { x, y, width, height } = doc.viewBox || {};
    if (!(width > 0 && height > 0)) {
      const minX = _.min(shapes.map(s => s.bbox.minX));
      const minY = _.min(shapes.map(s => s.bbox.minY));
      x = minX;
      y = minY;
      width = _.max(shapes.map(s => s.bbox.maxX)) - minX;
      height = _.max(shapes.map(s => s.bbox.maxY)) - minY;
    }
    const pad = Math.max(width, height) * 0.03;
    return `${x - pad} ${y - pad} ${width + 2 * pad} ${height + 2 * pad}`;
  }, [doc, shapes]);

  const selectedCount = _.filter(selected).length;
  const toggle = (i) => setSelected(sel => sel.map((v, j) => (j === i ? !v : v)));
  const setAll = (v) => setSelected(shapes.map(() => v));

  const pointCount = (s) => _.sumBy(s.subpaths, sp => sp.points.length);

  return <div style={{ minWidth: '700px', maxWidth: '90vw' }}>
    <div className={'d-flex'} style={{ gap: '12px', maxHeight: '65vh' }}>
      <div className={'flex-grow-1 bg-light rounded border'} style={{ minWidth: '380px', minHeight: '380px' }}>
        <svg viewBox={previewBox} style={{ width: '100%', height: '100%', maxHeight: '65vh' }}>
          {shapes.map((s, i) => {
            const on = selected[i];
            const color = shapeColor(i, shapes.length);
            return <path key={i} d={previews[i]}
                         fill={on ? color : 'none'} fillOpacity={hovered === i ? 0.7 : 0.3} fillRule={'evenodd'}
                         stroke={on ? color : '#999'} strokeWidth={hovered === i ? 3 : 1.5}
                         strokeDasharray={on ? null : '4 3'}
                         vectorEffect={'non-scaling-stroke'}
                         style={{ cursor: 'pointer' }}
                         onClick={() => toggle(i)}
                         onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}/>;
          })}
        </svg>
      </div>

      <div style={{ width: '280px', overflowY: 'auto' }}>
        <div className={'small text-secondary mb-1'}>
          {shapes.length} paths found. Click a path to toggle it.
          <a href={'#'} className={'ml-2'} onClick={(e) => { e.preventDefault(); setAll(true); }}>All</a>
          <a href={'#'} className={'ml-2'} onClick={(e) => { e.preventDefault(); setAll(false); }}>None</a>
        </div>

        {shapes.map((s, i) => <div key={i}
                                   className={'d-flex align-items-center px-1 rounded' + (hovered === i ? ' bg-light-primary' : '')}
                                   style={{ cursor: 'pointer', opacity: selected[i] ? 1 : 0.5 }}
                                   onClick={() => toggle(i)}
                                   onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
          <input type={'checkbox'} checked={selected[i]} onChange={() => toggle(i)} onClick={e => e.stopPropagation()}/>
          <span className={'inline-block ml-2 mr-2 rounded'}
                style={{ width: '12px', height: '12px', background: shapeColor(i, shapes.length), flex: 'none' }}/>
          <span className={'text-truncate'} style={{ flex: 1 }} title={s.label}>{s.label}</span>
          <span className={'small text-secondary ml-2 no-wrap'}>
            {s.subpaths.length > 1 ? `${s.subpaths.length} subpaths, ` : ''}{pointCount(s)} pts
          </span>
        </div>)}
      </div>
    </div>

    <div className={'d-flex align-items-center justify-content-between mt-2'}>
      <span title={'Off: one svg layer per path (each can be moved, scaled and edited on its own). On: everything in a single layer, inner paths become holes.'}>
        <SwitchInput value={merge} onChange={v => setMerge(typeof v === 'boolean' ? v : !merge)}>Merge into a single layer</SwitchInput>
      </span>

      <span>
        <button className={'btn btn-sm btn-secondary mr-2'} onClick={onCancel}>Cancel</button>
        <button className={'btn btn-sm btn-primary'} disabled={!selectedCount}
                onClick={() => onImport(shapes.filter((s, i) => selected[i]), merge)}>
          Import {merge ? '1 layer' : `${selectedCount} layer${selectedCount === 1 ? '' : 's'}`}
        </button>
      </span>
    </div>
  </div>;
}
